/* eslint-disable no-template-curly-in-string */
import * as _ from 'lodash';
import moment from 'moment';
import * as Yup from 'yup';
import axios from './axios';

const VALID_DATE = 'validation.validDate';
const REQUIRED_MESSAGE = 'validation.fieldIsRequired';
const MIN_MESSAGE = 'Debe ser mayor o igual a ${min}';
const MAX_MESSAGE = 'Debe ser menor o igual a ${max}';

Yup.setLocale({
  mixed: {
    required: REQUIRED_MESSAGE
  },
  number: {
    min: MIN_MESSAGE,
    max: MAX_MESSAGE
  }
});

const getErrorLabelForPath = (path, touched, errors, t) => {
  return _.get(touched, path) && _.get(errors, path) && t(_.get(errors, path));
};
const pathHasError = (path, touched, errors) =>
  !!(_.get(touched, path) && _.get(errors, path));
const getErrorLabelForPathObject = (path, touched, errors, valueKey, t) =>
  _.get(touched, path) &&
  _.get(errors, `${path}.${valueKey}`) &&
  t(_.get(errors, `${path}.${valueKey}`));
const pathHasErrorObject = (path, touched, errors, valueKey) =>
  !!(_.get(touched, path) && _.get(errors, `${path}.${valueKey}`));

const formatter = new Intl.NumberFormat('de-DE');
const toCurrency = value => {
  if (!value) {
    return value;
  }
  const number = value.replace(/[^0-9]/g, '');
  const converted = Number(number);
  return formatter.format(converted);
};

const toCurrencyNumber = value => {
  if (!value) {
    return value;
  }
  const number = value.replace(/[^0-9]/g, '');
  return Number(number);
};

const formatNumber = (
  number,
  minimumFractionDigits = 0,
  maximumFractionDigits = 2
) =>
  new Intl.NumberFormat('de-DE', {
    minimumFractionDigits,
    maximumFractionDigits
  }).format(number);

const textInputFormatNumber = numberString => {
  const lastChar = numberString.charAt(numberString.length - 1);
  const last2Chars = numberString.substring(
    numberString.length - 2,
    numberString.length
  );

  if (last2Chars === ',,' || last2Chars === ',.') {
    return `${numberString.substring(0, numberString.length - 1)}`;
  }

  // Don't format yet
  if (lastChar === ',' || last2Chars === ',0') {
    return numberString;
  }

  if (lastChar === '.') {
    return `${numberString.substring(0, numberString.length - 1)},`;
  }

  // Remove the third decimal digit instead of rounding to two decimal places
  const dotPosition = numberString.indexOf(',');
  if (dotPosition > 0 && dotPosition < numberString.length - 3) {
    return `${numberString.substring(0, numberString.length - 1)}`;
  }

  const parsed = parseFloat(numberString.replace(/\./g, '').replace(/,/g, '.'));
  if (Number.isNaN(parsed)) {
    return numberString;
  }
  return formatNumber(parsed);
};

const schemaWithDateTransform = Yup.date()
  .typeError(VALID_DATE)
  .required()
  .nullable();

const schemaCurrency = max => {
  let schema = Yup.mixed()
    .transform(value => {
      const number = Number(value);
      if (!Number.isNaN(number)) {
        return number;
      }
      return Number(value.replace(/\./g, ''));
    })
    .required()
    .test('number', 'Debe ser un número', value => {
      return !Number.isNaN(value);
    })
    .test('min', 'Debe ser mayor a 0', value => {
      return value > 0;
    });

  if (max) {
    schema = schema.test(
      'max',
      `No puede ser mayor a ${toCurrency(max)}`,
      value => {
        return value <= max;
      }
    );
  }
  return schema;
};

const schemaPorcentaje = max => {
  let schema = Yup.mixed()
    .transform(value => {
      const number = Number(value);
      if (!Number.isNaN(number)) {
        return number;
      }
      return parseFloat(
        `${value}`.replace(/\./g, '').replace(/,/g, '.')
      ).toFixed(2);
    })
    .test('number', 'Debe ser un número', value => {
      return !Number.isNaN(value);
    })
    .test('min', 'Debe ser mayor a 0', value => {
      return value === undefined || value > 0;
    });

  if (max) {
    schema = schema.test(
      'max',
      `No puede ser mayor a ${toCurrency(max)}`,
      value => {
        return value === undefined || value <= max;
      }
    );
  }
  return schema;
};

const schemaPrefijo = Yup.mixed()
  .test('min', 'Como mínimo debe contener 3 dígitos', value => {
    if (!value) {
      return true;
    }
    return value.replace(/_/g, '').length >= 3;
  })
  .test('max', 'Verificar formato', value => {
    if (!value) {
      return true;
    }
    return value.replace(/_/g, '').length <= 4;
  });

const schemaSufijo = Yup.string()
  .test('min', 'Como mínimo debe contener 5 dígitos', value => {
    if (!value) {
      return true;
    }
    return value.replace(/_/g, '').length >= 5;
  })
  .test('max', 'Verificar formato', value => {
    if (!value) {
      return true;
    }
    return value.replace(/_/g, '').length <= 7;
  });

const checkFileSize = file => {
  const filesize = (file.size / 1024 / 1024).toFixed(4); // MB
  if (filesize <= 10) {
    return true;
  }
  return false;
};

const schemaFile = required => {
  let schema = Yup.mixed()
    .test(
      'fileSize',
      'El archivo supera el máximo permitido (10 MB)',
      files => {
        if (files && files.length > 0) {
          return checkFileSize(files[0]);
        }
        return true;
      }
    )
    .test('fileType', 'Formato no soportado', files => {
      if (files && files.length > 0) {
        return ['application/pdf'].includes(files[0].type);
      }
      return true;
    });

  if (required) {
    schema = schema.test('required', 'Este campo es requerido', files => {
      if (files && files.length > 0) {
        return true;
      }
      return false;
    });
  }
  return schema;
};

// =============================================================================
// CALCULO DE DIGITO VERIFICADOR SEGUN SET en JS
// =============================================================================
const calcularDigitoVerificador = (paramNumero, paramBasemax = 11) => {
  const numero = String(paramNumero);
  const basemax = Number(paramBasemax);
  let total = 0;
  let resto = 0;
  let k = 0;
  let numeroAux = 0;
  let digit = 0;
  let numeroAl = '';
  for (let i = 0; i < numero.length; i += 1) {
    const c = Number(numero.charAt(i));
    numeroAl += c.toString();
  }
  k = 2;
  total = 0;
  for (let i = numeroAl.length - 1; i >= 0; i -= 1) {
    if (k > basemax) {
      k = 2;
    }
    numeroAux = Number(numeroAl.charAt(i));
    total += numeroAux * k;
    k += 1;
  }
  resto = total % 11;
  if (resto > 1) {
    digit = 11 - resto;
  } else {
    digit = 0;
  }
  return digit;
};

const checkIfDuplicateExists = w => {
  return new Set(w).size !== w.length;
};

const getFileDescripcion = codigo => {
  switch (codigo) {
    default:
      return codigo;
  }
};

const getFileDescripcionByError = message => {
  if (message.includes('documento')) {
    const mess = message.split(' ');

    const doc = getFileDescripcion(mess[2]);

    return message.replace(mess[2], `'${doc}'`);
  }
  return message;
};

const fetchPdfAsBase64 = async url => {
  try {
    const response = await axios.get(url, {
      responseType: 'arraybuffer' // Necesario para manejar binarios
    });

    // Crear un blob a partir de los datos del PDF
    const blob = new Blob([response.data], {
      type: response.headers['content-type']
    });

    // Convertir el blob a Base64 usando FileReader
    return await new Promise((resolve, reject) => {
      const reader = new FileReader();
      reader.onloadend = () => resolve(reader.result);
      reader.onerror = reject;
      reader.readAsDataURL(blob); // Devuelve una URL Base64
    });
  } catch (error) {
    console.error('Error fetching PDF:', error);
    throw error;
  }
};

const valorTotal = mercaderias => {
  const valor = mercaderias
    ? mercaderias
        .map(m => m.valorVenta)
        .reduce((tot, val) => tot + Number(val), 0)
    : 0;
  return formatNumber(
    Number(valor)
      .toFixed(2)
      .toLocaleString()
  );
};

const cantidadTotal = mercaderias => {
  const cantidad = mercaderias
    ? mercaderias
        .map(m => m.cantidad)
        .reduce((tot, val) => tot + Number(val), 0)
    : 0;
  return cantidad;
};

const loadFormulario = async idParam => {
  const response = await axios.get(`formularios/${idParam}/dto`);
  const nuevosDatos = { ...response.data };
  nuevosDatos.mercaderias = nuevosDatos.mercaderias.map(
    (mercaderia, index) => ({
      ...mercaderia,
      index: index + 1,
      factura: {
        nroFactura: mercaderia.nroCertificadoOrigen,
        fechaFactura: mercaderia.fechaFactura
      }
    })
  );

  nuevosDatos.valorTotal = valorTotal(nuevosDatos.mercaderias);
  nuevosDatos.totalDolares = formatNumber(
    nuevosDatos.totalDolares.toFixed(2).toLocaleString()
  );
  nuevosDatos.cantidadTotal = cantidadTotal(nuevosDatos.mercaderias);
  nuevosDatos.pesoTarifado = nuevosDatos.pesoTarifado
    ? formatNumber(
        Number(nuevosDatos.pesoTarifado)
          .toFixed(2)
          .toLocaleString()
      )
    : '';
  nuevosDatos.pesoEstimadoCarga = formatNumber(
    Number(nuevosDatos.pesoEstimadoCarga)
      .toFixed(2)
      .toLocaleString()
  );
  nuevosDatos.totalFlete = nuevosDatos.totalFlete
    ? formatNumber(
        Number(nuevosDatos.totalFlete)
          .toFixed(2)
          .toLocaleString()
      )
    : '';
  return nuevosDatos;
};

const loadEventos = async idParam => {
  const response = await axios.get(`formularios/${idParam}/estados`);
  const historico = response.data;
  historico.sort((a, b) => a.fecha - b.fecha);
  const nuevosEventos = historico.reduce((previous, current) => {
    return [
      ...previous,
      {
        fecha: moment(current.fecha),
        destino: current.idEstado
      }
    ];
  }, []);

  nuevosEventos.push({
    fecha: undefined,
    destino: (nuevosEventos[nuevosEventos.length - 1]?.destino || 0) + 1
  });
  return nuevosEventos;
};

export {
  calcularDigitoVerificador,
  checkIfDuplicateExists,
  fetchPdfAsBase64,
  formatNumber,
  getErrorLabelForPath,
  getErrorLabelForPathObject,
  getFileDescripcion,
  getFileDescripcionByError,
  loadEventos,
  loadFormulario,
  pathHasError,
  pathHasErrorObject,
  REQUIRED_MESSAGE,
  schemaCurrency,
  schemaFile,
  schemaPorcentaje,
  schemaPrefijo,
  schemaSufijo,
  schemaWithDateTransform,
  textInputFormatNumber,
  toCurrency,
  toCurrencyNumber,
  Yup
};
